import { cn } from "@ando/ui";
import "./AudioPlayer.css";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertTriangle, Loader2, Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

function timestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export interface AudioPlayerProps {
  src?: string;
  filename: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  className?: string;
}

/** The same compact voice-note controls in the composer and sent messages. */
export function AudioPlayer({
  src,
  filename,
  loading,
  error,
  onRetry,
  className,
}: AudioPlayerProps) {
  const { t } = useLingui();
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a refreshed source resets the playback state.
  useEffect(() => {
    setPlaying(false);
    setPosition(0);
    setDuration(0);
    setPlaybackError(false);
    setBuffering(false);
  }, [src]);
  useEffect(() => {
    if (!buffering) return;
    const timeout = window.setTimeout(() => {
      audio.current?.pause();
      setPlaying(false);
      setBuffering(false);
      setPlaybackError(true);
    }, 15000);
    return () => window.clearTimeout(timeout);
  }, [buffering]);
  const failed = error || playbackError;
  const busy = loading || buffering;
  const retry = () => {
    setPlaybackError(false);
    if (onRetry) onRetry();
    audio.current?.load();
  };
  const toggle = async () => {
    const element = audio.current;
    if (!element) return;
    if (!element.paused) element.pause();
    else {
      try {
        await element.play();
      } catch {
        setPlaybackError(true);
        setBuffering(false);
      }
    }
  };
  const updateDuration = (element: HTMLAudioElement) => {
    if (Number.isFinite(element.duration)) setDuration(element.duration);
    // Headerless MediaRecorder WebM can expose Infinity. Read the available
    // timeline rather than seeking far beyond the file, which stalls Safari.
    else if (element.seekable.length > 0)
      setDuration(element.seekable.end(element.seekable.length - 1));
  };
  return (
    <div
      className={cn("voice-note w-[280px] max-w-full rounded-xl bg-ink-1/[0.025] px-2.5 py-2", className)}
      data-testid="chat-file-audio"
      data-playing={playing && !busy ? "true" : "false"}
      title={filename}
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={!src || loading || failed}
          aria-label={playing ? t`Pausa audio` : t`Riproduci audio`}
          className="voice-note-play flex size-8 shrink-0 items-center justify-center rounded-full bg-ando-orange/[0.10] text-ando-orange transition-colors hover:bg-ando-orange/[0.18] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2 active:scale-95 disabled:opacity-40"
        >
          {busy ? (
            <Loader2 size={13} className="animate-spin" aria-hidden />
          ) : playing ? (
            <Pause size={13} fill="currentColor" aria-hidden />
          ) : (
            <Play size={13} fill="currentColor" className="ml-0.5" aria-hidden />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 font-medium text-ink-2 text-[11px]">
              <Trans>Messaggio vocale</Trans>
              <span className="voice-note-motion" aria-hidden="true"><i /><i /><i /></span>
            </span>
            <span className="text-[11px] text-ink-3 tabular-nums">
              {timestamp(position)} / {duration > 0 ? timestamp(duration) : "—:—"}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={Math.min(position, duration || 1)}
            disabled={!duration || failed || loading}
            aria-label={t`Posizione audio`}
            aria-valuetext={`${timestamp(position)} / ${timestamp(duration)}`}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (audio.current) audio.current.currentTime = next;
              setPosition(next);
            }}
            className="voice-note-seek my-1 block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
            style={{
              background: `linear-gradient(to right, var(--voice-note-accent, #ee7d48) ${duration > 0 ? (position / duration) * 100 : 0}%, rgba(100, 100, 100, 0.2) ${duration > 0 ? (position / duration) * 100 : 0}%)`,
            }}
          />
        </div>
      </div>
      {loading ? (
        <p role="status" className="mt-2 text-ink-3 text-xs">
          <Trans>Caricamento audio…</Trans>
        </p>
      ) : failed ? (
        <div role="alert" className="mt-2 flex items-center gap-2 text-ink-3 text-xs">
          <AlertTriangle size={13} aria-hidden />
          <span>
            <Trans>Impossibile riprodurre.</Trans>
          </span>
          <button
            type="button"
            onClick={retry}
            className="ml-auto rounded font-medium text-ando-orange hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
          >
            <Trans>Riprova</Trans>
          </button>
        </div>
      ) : null}
      {src ? (
        <audio
          ref={audio}
          src={src}
          preload="auto"
          className="hidden"
          aria-label={filename}
          onPlay={() => setPlaying(true)}
          onPause={() => {
            setPlaying(false);
            setBuffering(false);
          }}
          onEnded={() => {
            setPlaying(false);
            setBuffering(false);
          }}
          onWaiting={() => setBuffering(true)}
          onPlaying={() => setBuffering(false)}
          onCanPlay={() => setBuffering(false)}
          onError={() => {
            setPlaybackError(true);
            setPlaying(false);
            setBuffering(false);
          }}
          onDurationChange={(event) => updateDuration(event.currentTarget)}
          onLoadedMetadata={(event) => updateDuration(event.currentTarget)}
          onProgress={(event) => updateDuration(event.currentTarget)}
          onTimeUpdate={(event) => {
            const element = event.currentTarget;
            updateDuration(element);
            setPosition(element.currentTime);
          }}
        >
          <track kind="captions" srcLang="it" label="" default />
        </audio>
      ) : null}
    </div>
  );
}
